/**
 * ERPat's OWN attendance figures — the `metrics` block an attendance row
 * carries, and how to tell where it came from.
 *
 * Both attendance surfaces project the same block through the backend's
 * `AttendanceEuapiMetricsTrait::projectMetrics()`: the manager-level
 * `/v1/api/attendance` rows and, since HumanResource 1.38.0, every
 * `/v1/api/me/attendance*` row. The rule that fills it is the web Metrics
 * modal's (`Attendance::log_details()` → `AttendancesTrait::_row_metrics_only()`,
 * reached through `Attendance_metrics_service`): the STORED `attendance_metrics`
 * row for an approved record, a LIVE `BioMeet` computation for any other record
 * with a clock-out, and nothing for a punch still open. The row says which with
 * `metrics_source` (`stored` | `computed` | `none`) — {@link readRowMetrics}
 * reads the pair.
 *
 * Every figure is DECIMAL HOURS as sent (`8.5` = 8h 30m) except `duration`,
 * which the engine reports in SECONDS; {@link mapAttendanceMetrics} normalises
 * it so the VM has one unit.
 *
 * This lives in its own module — not `models.ts` — because `attendance-day.ts`
 * needs the mapper to file a punch under its ERPat log date, and `models.ts`
 * already imports `attendance-day.ts`. `models.ts` re-exports everything here,
 * so existing import sites are untouched.
 */
import { asRecord, pick, type Json } from '@/services/api/http';
import { formatDate, formatDurationMinutes } from '@/utils/format';

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const v = pick<unknown>(rec, keys, fallback);
  return v == null ? fallback : String(v);
};

/**
 * The OVERTIME review state — a SEPARATE decision from the attendance status,
 * made in ERPat's web "Attendance > Overtime" tab (gated on
 * `attendance_ot_approval`). Approving an attendance does not decide it; the
 * measured hours are held at `pending` until someone rules on them there.
 *
 * It matters for reading the numbers below: the overtime figures the API serves
 * are the PAYABLE ones, so every bucket reads 0 while the review is `pending` or
 * `rejected`. A 0 alone is ambiguous — "no overtime" and "overtime awaiting a
 * decision" look identical — which is why this ships alongside them.
 *
 * Fails OPEN to `approved`, matching the column default: an unset value is a row
 * written before the review feature existed, and its hours were always payable.
 */
export type AttendanceOtStatus = 'approved' | 'pending' | 'rejected';

/**
 * The overtime review state as the API reports it — the three decided states, plus
 * `''` meaning NO DECISION EXISTS YET. The empty value arrives only on a live
 * preview of a pending record (`metrics_source: "computed"`): no metrics row has
 * been written, so nobody has ruled on anything, and reporting `approved` there
 * would tell an approver the overtime was already authorised.
 */
export type AttendanceOtReviewState = AttendanceOtStatus | '';

/**
 * The six overtime buckets the review gate applies to, in the server's own order
 * (`Attendance_model::ot_gated_columns()`), plus their total.
 *
 * Individually addressable on purpose: the approval sheet renders the same
 * columns the web's metrics table does (Regular OT, Restday OT, Special OT HD,
 * Legal OT HD; then Special Restday / Legal Restday on the premiums row), and the
 * two-bucket roll-up this replaced could not express them.
 */
export interface AttendanceOtBuckets {
  regOt: number;
  restOt: number;
  specialOt: number;
  legalOt: number;
  specialRd: number;
  legalRd: number;
  /** Sum of the six. Taken from the server's own `total` when it sends one. */
  total: number;
}

/**
 * The computed payroll metrics for an attendance — STORED for an approved record
 * (the row payroll consumes), or a LIVE engine preview for a pending one (what an
 * approval would store; see `AttendanceApprovalVM.metricsPreview`). Null only
 * when the server could not produce either.
 *
 * Every figure is DECIMAL HOURS as sent (e.g. `8.5` = 8h 30m); format at the edge
 * with {@link attendanceHoursLabel}.
 */
export interface AttendanceMetricsVM {
  /** Effective log date the engine computed (YYYY-MM-DD; '' when unset). */
  logDate: string;
  /** '' on a preview — no decision has been recorded yet. */
  otStatus: AttendanceOtReviewState;

  // ── Core day ──
  /**
   * Clock-in to clock-out, before any deduction — in HOURS like every other
   * figure here. (The engine reports it in SECONDS; the mapper converts.)
   */
  duration: number;
  schedule: number;
  worked: number;
  /** Minutes-as-hours arrived late. */
  late: number;
  /** Overbreak — break time beyond the allowance. */
  over: number;
  /** Undertime — left before the scheduled end. */
  under: number;

  // ── Premium / non-overtime (never gated by the review) ──
  /** Night differential. */
  regNd: number;
  /** Special-holiday premium on SCHEDULED hours. Never gated, never withheld. */
  specialHd: number;
  /** Legal-holiday premium on scheduled hours. Never gated. */
  legalHd: number;
  /** Paid time off credited to the day. */
  ptoHr: number;

  // ── Overtime ──
  /**
   * The PAYABLE buckets. The server zeroes every one of them while the review is
   * `pending` or `rejected`, so these are what a payslip will show — and exactly
   * the wrong thing to put in front of a reviewer.
   */
  payableOt: AttendanceOtBuckets;
  /**
   * The MEASURED buckets (`measured_overtime`) — what the engine recorded and what
   * a reviewer is being asked to rule on. This is what the approval sheet renders,
   * matching the web's deliberate choice to show `raw_*` there.
   *
   * NEVER let a payroll figure come from here.
   */
  measuredOt: AttendanceOtBuckets;

  /**
   * True when overtime WAS measured but is not (yet) payable — the review is open
   * or was refused. Measured-led, not status-led: a preview always carries an
   * empty status, and calling a day with no overtime "withheld" would explain away
   * a 0 that is simply the truth.
   */
  otWithheld: boolean;

  // ── Overtime attribution ──
  /** '' when undecided; '0' when the tenant's standing policy decided it. */
  otCheckedById: string;
  /** Display name, "System" for a policy decision, '' when undecided. */
  otCheckedByName: string;
  /** Local "decided at" label ('' when undecided). */
  otCheckedAtLabel: string;
}

/** Decimal hours -> "8h 30m". Empty for zero/absent, so callers can `v-if`. */
export function attendanceHoursLabel(hours: unknown): string {
  const n = typeof hours === 'number' ? hours : Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '';
  return formatDurationMinutes(n * 60);
}

/**
 * The review state as sent. `''` is preserved (a preview's "no decision"); any
 * other unrecognised value fails OPEN to `approved`, matching the column default —
 * an unset value on a stored row predates the review feature and was always payable.
 */
export function normalizeOtReviewState(value: unknown): AttendanceOtReviewState {
  const s = String(value ?? '').toLowerCase().trim();
  if (s === '') return '';
  return s === 'pending' || s === 'rejected' ? s : 'approved';
}

/** Decimal-hours reader — a missing key is 0 hours, never a crash. */
export function otHours(rec: Json, keys: string[]): number {
  const n = Number(pick<unknown>(rec, keys, 0));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Read six buckets off a flat record, optionally through a key prefix.
 *
 * `prefix` exists for the `raw_` aliases: a database still awaiting the overtime
 * migration has no `measured_overtime` object, and the backend's own reader falls
 * back to the gated column in that case. Mirror that fallback here so the two
 * degrade the same way — toward "nothing to review", never toward paying
 * unreviewed hours.
 */
export function readOtBuckets(rec: Json, prefix = ''): AttendanceOtBuckets {
  const at = (key: string): number => (prefix ? otHours(rec, [`${prefix}${key}`, key]) : otHours(rec, [key]));
  const regOt = at('reg_ot');
  const restOt = at('rest_ot');
  const specialOt = at('special_ot');
  const legalOt = at('legal_ot');
  const specialRd = at('special_rd');
  const legalRd = at('legal_rd');
  const sent = otHours(rec, ['total', 'ot_total', 'raw_ot_total']);
  return {
    regOt, restOt, specialOt, legalOt, specialRd, legalRd,
    // The server computes `total` as the sum of exactly these six and says not to
    // re-sum it; fall back to the sum only when it did not send one.
    total: sent || regOt + restOt + specialOt + legalOt + specialRd + legalRd,
  };
}

/**
 * Map the `metrics` block the detailed attendance shape carries, or null when the
 * record has none. Read defensively like the rest of this file — a missing key is
 * 0 hours, not a crash.
 */
export function mapAttendanceMetrics(value: unknown): AttendanceMetricsVM | null {
  if (value == null) return null;
  const m = asRecord(value);
  if (!Object.keys(m).length) return null;

  const hours = (key: string): number => otHours(m, [key]);

  const payableOt = readOtBuckets(m);
  // Prefer the server's dedicated object; fall back to `raw_*` on the record; fall
  // back again to the payable buckets — the same ladder the backend climbs.
  const measuredRaw = asRecord(pick(m, ['measured_overtime', 'measuredOvertime', 'raw_overtime'], {}));
  const measuredOt = Object.keys(measuredRaw).length ? readOtBuckets(measuredRaw) : readOtBuckets(m, 'raw_');

  const otStatus = normalizeOtReviewState(pick(m, ['ot_status'], ''));
  const otCheckedAtLocal = str(m, ['ot_checked_at_local'], '');
  const otCheckedByRaw = pick<unknown>(m, ['ot_checked_by'], null);

  return {
    logDate: str(m, ['log_date'], ''),
    otStatus,
    // The one figure the engine reports in SECONDS (BioMeet::getTotalDuration()),
    // stored and served as such. Normalised to hours so the VM has one unit.
    duration: hours('duration') / 3600,
    schedule: hours('schedule'),
    worked: hours('worked'),
    late: hours('late'),
    over: hours('over'),
    under: hours('under'),
    regNd: hours('reg_nd'),
    specialHd: hours('special_hd'),
    legalHd: hours('legal_hd'),
    ptoHr: hours('pto_hr'),
    payableOt,
    measuredOt,
    otWithheld: otStatus !== 'approved' && measuredOt.total > 0,
    otCheckedById: otCheckedByRaw == null ? '' : String(otCheckedByRaw),
    otCheckedByName: str(m, ['ot_checked_by_name'], ''),
    otCheckedAtLabel: otCheckedAtLocal ? formatDate(otCheckedAtLocal.replace(' ', 'T')) : '',
  };
}

// ── Provenance ───────────────────────────────────────────────────────────────
/**
 * Where a row's `metrics` block came from, as the server's `metrics_source`
 * discriminator names it:
 *   stored   — the `attendance_metrics` row payroll consumes (an approved record);
 *   computed — a live `BioMeet` run for this request (any other clocked-out
 *              record; what an approval would store, not what has been stored);
 *   none     — no figures at all: the punch is still open, or the engine could
 *              not run. The device measures such a row itself.
 */
export type MetricsSource = 'stored' | 'computed' | 'none';

/** An attendance row's figures and their provenance, read together. */
export interface RowMetrics {
  source: MetricsSource;
  /** Null exactly when `source` is `none`. */
  metrics: AttendanceMetricsVM | null;
}

/**
 * Read one attendance row's `metrics` + `metrics_source` pair defensively.
 *
 * A backend that predates the discriminator sends the block alone: it can only
 * be the stored row when the record is `approved` (nothing else has one), so any
 * other status reads `computed`. A declared source with no block behind it is
 * `none` — there is nothing to prefer over the device's own measurement.
 *
 * A `computed` block whose `log_date` is blank and whose `duration` and
 * `schedule` are both 0 is `BioMeet::calculate()`'s EARLY RETURN (it bails before
 * measuring when the record has no usable clock-out), not a figure. It is
 * treated as `none` so a zeroed preview can never beat the device's live
 * accrual on a shift still in progress.
 *
 * A `clockout` row is `none` too, whatever it declares. The auto clock-out job
 * (`AttendanceAutoClockinAndOutJob`) stamps that status on a punch nobody closed
 * and sets `out_time = in_time` — a placeholder, not a departure — so the engine
 * can run on it (it has an out_time) but measures a zero-length shift. The
 * server's own summary skips such rows; treating the block as absent lets the
 * device's rule apply instead (a past open punch contributes nothing). The
 * same goes for a LIVE block on any row whose out stamp equals its in stamp,
 * however it is labelled — only the STORED payroll row is never second-guessed.
 */
export function readRowMetrics(row: Json): RowMetrics {
  const r = asRecord(row);
  const status = str(r, ['status'], '').toLowerCase().trim();
  if (status === 'clockout') return { source: 'none', metrics: null };

  const metrics = mapAttendanceMetrics(pick<unknown>(r, ['metrics'], null));
  const declared = str(r, ['metrics_source', 'metricsSource'], '').toLowerCase().trim();

  let source: MetricsSource;
  if (declared === 'stored' || declared === 'computed' || declared === 'none') source = declared;
  else if (metrics == null) source = 'none';
  else source = status === 'approved' ? 'stored' : 'computed';

  if (metrics == null) return { source: 'none', metrics: null };
  if (source !== 'stored') {
    const inStamp = str(r, ['in_time_local', 'in_time'], '').trim();
    const outStamp = str(r, ['out_time_local', 'out_time'], '').trim();
    if (inStamp !== '' && outStamp === inStamp) return { source: 'none', metrics: null };
  }
  if (source === 'none') return { source, metrics: null };
  if (source === 'computed' && metrics.logDate === '' && metrics.duration === 0 && metrics.schedule === 0) {
    return { source: 'none', metrics: null };
  }
  return { source, metrics };
}
